import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Layout } from "./components/Layout";
import { AdminLayout } from "./components/AdminLayout";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminLogin } from "./pages/admin/AdminLogin";
import { SchedulePage } from "./pages/SchedulePage";
import { BookingPage } from "./pages/BookingPage";
import { PaymentSuccessPage } from "./pages/PaymentSuccessPage";
import { LookupBookingPage } from "./pages/LookupBookingPage";
import { EventsPage } from "./pages/EventsPage";
import { EventDetailPage } from "./pages/EventDetailPage";
import { PackagesPage } from "./pages/PackagesPage";
import { PackagePurchasePage } from "./pages/PackagePurchasePage";
import { SupportPage } from "./pages/SupportPage";
import { PrivateSessionPage } from "./pages/PrivateSessionPage";
import { StudioBookingPage } from "./pages/StudioBookingPage";
import { PaymentProcessingPage } from "./pages/PaymentProcessingPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminBookings } from "./pages/admin/AdminBookings";
import { AdminClasses } from "./pages/admin/AdminClasses";
import { AdminSchedules } from "./pages/admin/AdminSchedules";
import { AdminPackages } from "./pages/admin/AdminPackages";
import { AdminPackageManagement } from "./pages/admin/AdminPackageManagement";
import { AdminEvents } from "./pages/admin/AdminEvents";
import { AdminEventRoster } from "./pages/admin/AdminEventRoster";
import { AdminInstructors } from "./pages/admin/AdminInstructors";
import { AdminAttendance } from "./pages/admin/AdminAttendance";
import { AdminClassRoster } from "./pages/admin/AdminClassRoster";
import { AdminActivities } from "./pages/admin/AdminActivities";
import { AdminReports } from "./pages/admin/AdminReports";
import { AdminCampaigns } from "./pages/admin/AdminCampaigns";
import { AdminPrivateSessions } from "./pages/admin/AdminPrivateSessions";
import { AdminStudioBookings } from "./pages/admin/AdminStudioBookings";
import { AdminClients } from "./pages/admin/AdminClients";
import { AdminSupport } from "./pages/admin/AdminSupport";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
}

function App() {
  return (
    <div className="App min-h-screen bg-black">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<SchedulePage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/lookup" element={<LookupBookingPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/packages" element={<PackagesPage />} />
              <Route path="/packages/:slug" element={<PackagePurchasePage />} />
              <Route path="/private-sessions" element={<PrivateSessionPage />} />
              <Route path="/studio-booking" element={<StudioBookingPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/payment-processing" element={<PaymentProcessingPage />} />
            </Route>

            {/* Admin login — public */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin Routes — JWT protected */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="classes" element={<AdminClasses />} />
              <Route path="schedules" element={<AdminSchedules />} />
              <Route path="instructors" element={<AdminInstructors />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="package-management" element={<AdminPackageManagement />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="event-roster" element={<AdminEventRoster />} />
              <Route path="private-sessions" element={<AdminPrivateSessions />} />
              <Route path="studio-bookings" element={<AdminStudioBookings />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="attendance" element={<AdminAttendance />} />
              <Route path="class-roster" element={<AdminClassRoster />} />
              <Route path="activities" element={<AdminActivities />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all — public 404 */}
            <Route element={<Layout />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        theme="dark"
        richColors
        closeButton
        expand
        gap={10}
        toastOptions={{
          duration: 5000,
          classNames: {
            toast:
              "sonner-toast-empire !bg-neutral-950 !text-white !border !shadow-xl !rounded-xl !p-4",
            title: "!text-white !font-semibold !text-[15px] !leading-snug",
            description:
              "!text-neutral-300 !text-[13px] !leading-relaxed !opacity-100 !mt-1",
            actionButton:
              "!bg-[#d7ac04] !text-black !font-semibold hover:!opacity-90",
            cancelButton:
              "!bg-neutral-800 !text-neutral-100 !border !border-neutral-600 hover:!bg-neutral-700",
            closeButton:
              "!bg-neutral-800/80 !text-neutral-300 !border-neutral-700 hover:!text-white",
            success: "!border-green-500/40",
            error: "!border-red-500/45",
            info: "!border-[#d7ac04]/45",
            warning: "!border-amber-500/45",
            loading: "!border-[#d7ac04]/40",
          },
        }}
      />
    </div>
  );
}

export default App;
